import React from 'react';
import { Grid, Paper, Typography, Box, Skeleton, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    Receipt,
    AttachMoney,
    PersonAdd,
    ShoppingCart,
    Cancel,
    Replay,
} from '@mui/icons-material';

import { useGetReportKpis } from '../../hooks/reports/useGetReportKpis';
import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';

const KpiCard = ({ icon: Icon, color, label, value, isLoading }) => {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${color}.lighter`,
                    color: `${color}.main`,
                    flexShrink: 0,
                }}
            >
                <Icon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {label}
                </Typography>
                {isLoading ? (
                    <Skeleton variant="text" width="60%" height={32} />
                ) : (
                    <Typography variant="h6" fontWeight={700} noWrap>
                        {value}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

const KpiCardsRow = ({ filter }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice, locale } = useCurrency();

    const { kpis, isLoading } = useGetReportKpis({
        from: filter.from,
        to: filter.to,
    });

    const cards = [
        {
            key: 'revenue',
            icon: AttachMoney,
            color: 'success',
            label: isArabic ? 'إجمالي الإيرادات' : 'Revenue',
            value: formatPrice(kpis?.revenue ?? 0),
        },
        {
            key: 'orders_count',
            icon: Receipt,
            color: 'primary',
            label: isArabic ? 'عدد الطلبات' : 'Orders',
            value: formatNumber(kpis?.orders_count ?? 0, locale),
        },
        {
            key: 'avg_order_value',
            icon: ShoppingCart,
            color: 'info',
            label: isArabic ? 'متوسط قيمة الطلب' : 'Avg Order Value',
            value: formatPrice(kpis?.avg_order_value ?? 0),
        },
        {
            key: 'new_customers',
            icon: PersonAdd,
            color: 'secondary',
            label: isArabic ? 'عملاء جدد' : 'New Customers',
            value: formatNumber(kpis?.new_customers ?? 0, locale),
        },
        {
            key: 'cancelled_count',
            icon: Cancel,
            color: 'warning',
            label: isArabic ? 'طلبات ملغية' : 'Cancelled Orders',
            value: formatNumber(kpis?.cancelled_count ?? 0, locale),
        },
        {
            key: 'refunded_amount',
            icon: Replay,
            color: 'error',
            label: isArabic ? 'مبالغ مستردة' : 'Refunded',
            value: formatPrice(kpis?.refunded_amount ?? 0),
        },
    ];

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {cards.map((c) => (
                <Grid key={c.key} item xs={12} sm={6} md={4} lg={2}>
                    <KpiCard {...c} isLoading={isLoading} />
                </Grid>
            ))}
        </Grid>
    );
};

export default KpiCardsRow;
